import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import IconButton from '@mui/material/IconButton';
import React, { useState, ChangeEvent, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MAX_PAGE_LIMIT } from '../../constants/numbers';
import { filterList } from '../../utils/array';
import { ICampaign } from '../../interfaces/campaign';
import IResponse, { IPagination } from '../../interfaces/response';
import AdminDataGrid from './AdminDataGrid';
import { CampaignContext } from '../../contexts/CampaignContext';
import ShowModalCampaign from '../admin/campaign/campaignList/ShowModalCampaign';

interface MediaEntetiesProps {
	media_id: number;
}

const MediaEntetiesTable: React.FC<MediaEntetiesProps> = ({ media_id }) => {
	let { fetchedMedias } = useContext(CampaignContext);
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);
	const [fetchedData, setFetchedData] = useState<ICampaign[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	let [open, setOpen] = useState<boolean>(false);
	let [status, setStatus] = useState<string>('');
	let [id, setId] = useState<number | undefined>(undefined);

	let sendStatusCampaign = (status: string, id: number) => {
		setId(id);
		setOpen(true);
		setStatus(status);
	};

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleViewCampaign = (campaignId: string) => () => {
		navigate(`/my-social-media/edit/${campaignId}`);
	};

	const handleDeleteCampaign = (campaignId: string) => () => {
		axios<IResponse<boolean>>({
			url: `/campaigns/${parseInt(campaignId)}`,
			method: 'DELETE',
		}).then(res => {
			if (res.data.success) {
				fetchCampaigns();
			}
		});
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const fetchCampaigns = () => {
		setLoading(true);
		axios<IResponse<ICampaign[]>>({
			url: '/admin/media-entities',
			params: {
				limit: pageSize,
				media_id: media_id,
				page: page,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleCreateCampaign = () => {
		navigate('/campaigns/create');
	};

	const properties: (keyof ICampaign)[] = ['title', 'content_type', 'media', 'status'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<ICampaign>[] = [
		{ field: 'title', flex: 1, headerName: t('page_name'), headerAlign: 'center', align: 'center', headerClassName: headerClassNames },
		{ field: 'admin_name', flex: 1, headerName: t('admin_name'), headerAlign: 'center', align: 'center', headerClassName: headerClassNames },
		{
			field: 'media',
			flex: 1,
			headerName: t('media'),
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: (params: any) => {
				let filterMedia = fetchedMedias?.find(item => item.system_name === params.row.media);
				return filterMedia?.title;
			},
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 150,
			flex: 1,
			display: 'flex',
			headerClassName: `${headerClassNames}`,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				return <div className={`rounded-lg p-2.5 ${params.row.status === 'confirmed' ? 'bg-green-50 text-green-600' : params.row.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>{params.row.status === 'confirmed' ? t('confirmed') : params.row.status === 'rejected' ? t('rejected') : t('confirmation_pend')}</div>;
			},
		},
		// {
		// 	field: 'view_campaign',
		// 	headerName: t('view_in_search'),
		// 	flex: 2,
		// 	display: 'flex',
		// 	headerClassName: `${headerClassNames}`,
		// 	headerAlign: 'center',
		// 	align: 'center',
		// 	renderCell: (params: any) => (
		// 		<FormGroup className="flex flex-row items-center justify-center pt-2">
		// 			<FormControlLabel control={<Checkbox checked={params.row.view_campaign} />} label={t('view')} />
		// 			<FormControlLabel control={<Checkbox checked={params.row.reserve_campaign} />} label={t('reserve')} />
		// 			<FormControlLabel control={<Checkbox checked={params.row.influencer_campaign} />} label={t('influenceral')} />
		// 		</FormGroup>
		// 	),
		// },
		{
			field: '',
			headerName: t('campaign_type'),
			width: 150,
			flex: 1,
			display: 'flex',
			headerClassName: `${headerClassNames}`,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				if (params.row.status === 'pending') {
					return (
						<div>
							<button className="mx-1 w-12 rounded-md bg-green-400 p-2 text-white" onClick={() => sendStatusCampaign('update-status', params.row.id)}>
								{t('confirm')}
							</button>
							<button className="mx-1 w-12 rounded-md bg-red-500 p-2 text-white" onClick={() => sendStatusCampaign('reject', params.row.id)}>
								{t('rejected')}
							</button>
						</div>
					);
				} else {
					return <div>___</div>;
				}
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			flex: 1,
			headerClassName: headerClassNames,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params: any) => {
				const isDone = params.row.status === 'completed';
				return (
					<>
						<IconButton onClick={handleViewCampaign(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleDeleteCampaign(params.row.id)} disabled={isDone}>
							<DeleteOutlineOutlinedIcon className={isDone ? 'text-text-disabled' : 'text-text-secondary'} />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		fetchCampaigns();
	}, [page, pageSize]);

	return (
		<>
			<AdminDataGrid<ICampaign> pagination={pagination} loading={loading} rows={filteredRows} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={handleCreateCampaign} actionButtonLable={t('create_new_campaign')} searchableFields={properties} />;
			<ShowModalCampaign open={open} status={status} id={id} setOpen={setOpen} />
		</>
	);
};

export default MediaEntetiesTable;
